'use client';

import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';

import { useUserSession } from '@libs/common-next';

import {
    acceptProfileCompleteCelebrationLanding,
    clearProfileCompleteCelebrationLeavePending,
    destroyProfileCompleteCelebration,
    hasProfileCompleteCelebration,
    hasProfileCompleteCelebrationHandoff,
    hasProfileCompleteCelebrationLeavePending,
    isCelebrationLandingPath,
    isCompleteProfilePath,
    markProfileCompleteCelebrationLeavePending,
} from './celebration';
import { getProfileCelebrationUserScope } from './celebration-user-scope';

const LEGACY_NAVIGATION_TYPE_RELOAD = 1;
const LEGACY_NAVIGATION_TYPE_BACK_FORWARD = 2;

function pathnameSegments(pathname: string): string[] {
    return pathname.split('/').filter(Boolean);
}

function matchesExactTail(segments: string[], tail: readonly string[]): boolean {
    const start = segments.length - tail.length;
    if (start < 0) {
        return false;
    }

    return tail.every((segment, index) => segments[start + index] === segment);
}

export function isProfileCelebrationSurface(pathname: string): boolean {
    const segments = pathnameSegments(pathname);

    return (
        matchesExactTail(segments, ['account', 'about-you']) ||
        matchesExactTail(segments, ['treats-rewards', 'authenticated'])
    );
}

function getLegacyNavigationType(): PerformanceNavigationTiming['type'] | null {
    try {
        const legacyType = performance.navigation?.type;
        if (legacyType === LEGACY_NAVIGATION_TYPE_RELOAD) {
            return 'reload';
        }
        if (legacyType === LEGACY_NAVIGATION_TYPE_BACK_FORWARD) {
            return 'back_forward';
        }
        if (legacyType === 0) {
            return 'navigate';
        }
    } catch {
        return null;
    }

    return null;
}

function getNavigationType(): PerformanceNavigationTiming['type'] | null {
    try {
        const entry = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
        if (entry?.type) {
            return entry.type;
        }
    } catch {
        // Fall through to the deprecated PerformanceNavigation API.
    }

    return getLegacyNavigationType();
}

function shouldKeepCelebrationOnRemount(pathname: string): boolean {
    if (isCompleteProfilePath(pathname)) {
        return true;
    }

    if (!isProfileCelebrationSurface(pathname) && !isCelebrationLandingPath(pathname)) {
        return false;
    }

    return getNavigationType() === 'reload' || hasProfileCompleteCelebrationHandoff();
}

function markLeavePendingIfOnCelebrationSurface(pathname: string | null | undefined): void {
    if (pathname && isProfileCelebrationSurface(pathname) && hasProfileCompleteCelebration()) {
        markProfileCompleteCelebrationLeavePending();
    }
}

function resolvePendingLeave(
    pathname: string | null | undefined,
    userScope: string | null,
    onDismiss?: () => void
): void {
    if (!hasProfileCompleteCelebrationLeavePending()) {
        return;
    }

    if (!pathname) {
        return;
    }

    if (shouldKeepCelebrationOnRemount(pathname)) {
        if (isCelebrationLandingPath(pathname) || isProfileCelebrationSurface(pathname)) {
            acceptProfileCompleteCelebrationLanding(pathname);
            return;
        }

        clearProfileCompleteCelebrationLeavePending();
        return;
    }

    if (hasProfileCompleteCelebration()) {
        destroyProfileCompleteCelebration(userScope);
        onDismiss?.();
        return;
    }

    clearProfileCompleteCelebrationLeavePending();
}

function isPersistedPageShow(event: Event): boolean {
    return 'persisted' in event && Boolean((event as PageTransitionEvent).persisted);
}

type UseDismissProfileCelebrationOutsideMyAccountOptions = {
    enabled?: boolean;
    onDismiss?: () => void;
};

export function useDismissProfileCelebrationOutsideMyAccount({
    enabled = true,
    onDismiss,
}: UseDismissProfileCelebrationOutsideMyAccountOptions = {}): void {
    const pathname = usePathname();
    const { user } = useUserSession();
    const userScope = getProfileCelebrationUserScope(user);
    const onDismissRef = useRef(onDismiss);
    onDismissRef.current = onDismiss;
    const pathnameRef = useRef(pathname);
    pathnameRef.current = pathname;
    const userScopeRef = useRef(userScope);
    userScopeRef.current = userScope;
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    useEffect(() => {
        if (!enabled) {
            return;
        }

        resolvePendingLeave(pathnameRef.current, userScopeRef.current, () => onDismissRef.current?.());

        const handlePageHide = () => {
            markLeavePendingIfOnCelebrationSurface(pathnameRef.current);
        };

        const handlePageShow = (event: Event) => {
            if (!enabledRef.current || !isPersistedPageShow(event) || !hasProfileCompleteCelebrationLeavePending()) {
                return;
            }

            if (hasProfileCompleteCelebration()) {
                destroyProfileCompleteCelebration(userScopeRef.current);
                onDismissRef.current?.();
                return;
            }

            clearProfileCompleteCelebrationLeavePending();
        };

        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('pageshow', handlePageShow);

        let isStablyMounted = false;
        const stableMountFrame = requestAnimationFrame(() => {
            isStablyMounted = true;
        });

        return () => {
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('pageshow', handlePageShow);
            cancelAnimationFrame(stableMountFrame);
            if (!isStablyMounted || !enabledRef.current) {
                return;
            }

            markLeavePendingIfOnCelebrationSurface(pathnameRef.current);
        };
    }, [enabled]);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        if (hasProfileCompleteCelebrationLeavePending()) {
            resolvePendingLeave(pathname, userScope, () => onDismissRef.current?.());
            return;
        }

        if (!pathname || isCompleteProfilePath(pathname)) {
            return;
        }

        if (!hasProfileCompleteCelebration()) {
            return;
        }

        if (isProfileCelebrationSurface(pathname) || isCelebrationLandingPath(pathname)) {
            acceptProfileCompleteCelebrationLanding(pathname);
            return;
        }

        destroyProfileCompleteCelebration(userScope);
        onDismissRef.current?.();
    }, [enabled, pathname, userScope]);
}
